import React, { useState } from 'react';
import Dialog from '@dashboard/components/Dialog';
import { Form } from '@dashboard/components/Formik';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { fetcher } from '@dashboard/services/request';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { useToast } from '@dashboard/context/ToastContext';
import { apiKeysKey } from '@dashboard/lib/queryKeys';
import Callout from '@/dashboard/components/Callout';
import CopyableText from '@/dashboard/components/CopyableText';

const ApiKeyCreateDialog = ({ open, onClose }) => {
  const [createdApiKey, setCreatedApiKey] = useState(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const { sellerId, token } = useAuth();
  const { emitToast } = useToast();
  const queryClient = useQueryClient();

  const createApiKey = useMutation({
    mutationFn: values =>
      fetcher(`/sellers/${sellerId}/api-keys`, token, {
        method: 'POST',
        body: JSON.stringify({
          ...values,
          permissions: ['admin:read', 'admin:write'],
          scopes: ['public'],
        }),
      }),
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: apiKeysKey(sellerId) });
      setCreatedApiKey(data);
      setShowSuccessDialog(true);
      onClose();
    },
    onError: () => {
      emitToast({
        title: 'Failed to create API key',
        type: 'error',
      });
    },
  });

  const handleSuccessDialogClose = () => {
    setShowSuccessDialog(false);
    setCreatedApiKey(null);
  };

  return (
    <>
      <Dialog
        title="Create API key"
        cancelButton={null}
        confirmButton={null}
        size="lg"
        open={open}
        onCancel={onClose}
        description="Create a new API key to access the Checkout Page API."
      >
        {() => (
          <Form
            showState={false}
            initialValues={{
              name: '',
              expiresAt: '',
              isLive: true,
            }}
            onSubmit={async values => {
              try {
                const submitData = {
                  name: values.name,
                };

                if (values.expiresAt) {
                  submitData.expiresAt = new Date(values.expiresAt).toISOString();
                }

                const data = await createApiKey.mutateAsync(submitData);
                return { data };
              } catch (error) {
                return { error };
              }
            }}
            fields={() => [
              {
                name: 'name',
                label: 'API key name',
                type: 'text',
                required: true,
                placeholder: 'My API key',
              },
              {
                name: 'expiresAt',
                label: 'Expires at (optional)',
                type: 'datetime-local',
                tags: ['Leave empty for no expiration'],
              },
            ]}
            formSubmitAlign="end"
            submitButton={{
              label: 'Create API key',
              appearance: 'primary',
            }}
          />
        )}
      </Dialog>

      <Dialog
        title="API key created"
        cancelButton={null}
        confirmButton={{
          label: 'I saved the API key',
          appearance: 'primary',
          onClick: handleSuccessDialogClose,
        }}
        size="xl"
        open={showSuccessDialog}
        onCancel={handleSuccessDialogClose}
      >
        <Callout variant="warning">
          <Callout.Heading>This is the only time you'll see this key</Callout.Heading>
          <Callout.Content>
            Make sure to copy it now. You won't be able to see it again.
          </Callout.Content>
        </Callout>

        {createdApiKey && <CopyableText text={createdApiKey.apiKey} label="API Key" />}
      </Dialog>
    </>
  );
};

export default ApiKeyCreateDialog;
